import React, { useEffect, useState } from "react";
import { Wallet, ChevronRight } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, fmtINR, fmtUSD } from "@/lib/api";

const LABEL = { image: "Image", video: "Video", audio: "Audio", remix: "Remix", blackfx: "Black FX" };

const Row = ({ r, total, onOpen }) => (
  <button type="button" onClick={onOpen} data-testid={`investment-row-${r.id}`}
    className="w-full text-left p-3 rounded-xl border border-zinc-800 bg-[#0D1216]/70 hover:border-[#5FC0C8]/50 transition-colors">
    <div className="flex items-center justify-between gap-3">
      <span className="font-medium text-white truncate">{r.name}</span>
      <span className="font-heading font-bold text-white whitespace-nowrap" data-testid={`investment-row-inr-${r.id}`}>{fmtINR(r.inr)}</span>
    </div>
    <div className="h-1 mt-2 rounded-full bg-zinc-800 overflow-hidden">
      <div className="h-full ax-gradient-bg" style={{ width: `${total ? Math.max(2, (r.usd / total) * 100) : 0}%` }} />
    </div>
    <div className="flex flex-wrap items-center gap-1.5 mt-2">
      <span className="text-[11px] font-mono text-zinc-500 mr-1">{fmtINR(r.inr)} · {fmtUSD(r.usd)} · {r.count} generations</span>
      {Object.entries(r.by_section_inr || {}).filter(([, v]) => v > 0).map(([k, v]) => (
        <span key={k} className="ax-badge" data-testid={`investment-row-${r.id}-${k}`}>{LABEL[k] || k} {fmtINR(v)}{r.inr ? ` · ${fmtUSD((v / r.inr) * r.usd)}` : ""}</span>
      ))}
    </div>
  </button>
);

export default function InvestmentCard({ onOpenProject, refreshKey }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => { api.get("/spend/summary").then((r) => setData(r.data)).catch(() => {}); }, [refreshKey]);
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} data-testid="investment-card"
        className="ax-card ax-glass-card px-5 py-4 flex items-center gap-4 text-left hover:border-[#5FC0C8]/50 transition-colors">
        <span className="h-11 w-11 rounded-xl bg-[#7AF25F]/10 border border-[#7AF25F]/30 flex items-center justify-center"><Wallet className="w-5 h-5 text-[#7AF25F]" /></span>
        <span className="flex-1">
          <span className="block text-[10px] font-mono uppercase tracking-widest text-zinc-500">Total investment · all projects</span>
          <span className="flex items-baseline gap-2">
            <span className="text-2xl font-heading font-bold ax-gradient-text" data-testid="investment-total-inr">{data ? fmtINR(data.inr) : "…"}</span>
            <span className="text-xs font-mono text-zinc-400" data-testid="investment-total-usd">{data ? fmtUSD(data.usd) : ""}</span>
          </span>
        </span>
        <ChevronRight className="w-4 h-4 text-zinc-500" />
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0B1013] border-zinc-800 text-zinc-100 max-w-xl" data-testid="investment-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl">Investment by project</DialogTitle></DialogHeader>
          <div className="text-sm text-zinc-400 -mt-2">
            {data && <>Total <b className="text-white">{fmtINR(data.inr)}</b> ({fmtUSD(data.usd)}) across {data.projects.length} projects · {data.count} generations</>}
          </div>
          <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-1" data-testid="investment-list">
            {data?.projects.map((r) => <Row key={r.id} r={r} total={data.usd} onOpen={() => { setOpen(false); onOpenProject(r.id); }} />)}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
